import { Compass, Layers, Repeat } from 'lucide-react';
import { Question, Section } from 'src/types/types.ts';

export const SECTIONS: Section[] = [
  {
    id: 'strategie',
    title: 'Strategie zur Kundenzentrierung',
    kicker: 'Ebene 1 von 3',
    intro: 'Ist Kundenzentrierung bei uns verankert?',
    icon: Compass,
    subgroups: [
      {
        id: 'strategie-main',
        title: null,
        questions: [
          {
            id: 'ST-01',
            text: 'Kundenzentrierung wird als Toppriorität sichtbar vorgelebt – sie steht regelmäßig auf der Agenda von Topmanagement- und Vorstandsrunden und wird aktiv geführt, nicht nur delegiert.'
          },
          { id: 'ST-02', text: 'Kundenzentrierung ist fester Bestandteil unserer Strategie.' },
          {
            id: 'ST-03',
            text: 'Zielkonflikte wie Ertrag vs. Kundennutzen sind in unserer Strategie klar besprochen und priorisiert.'
          },
          {
            id: 'ST-04',
            text: 'Klare strategische Leitplanken zur Kundenzentrierung sind bekannt und geben Orientierung im Alltag.'
          },
          {
            id: 'ST-05',
            text: 'Aus den Leitplanken werden priorisierte Handlungsfelder mit klaren Verantwortlichkeiten und Zielen abgeleitet.'
          },
          {
            id: 'ST-06',
            text: 'Kundenzufriedenheit entsteht bei uns nicht durch einzelne Highlights, sondern durch verlässliche, wiederholbare Qualität im Kundenerlebnis.'
          },
          {
            id: 'ST-07',
            text: 'Kundenzentrierung zeigt sich daran, dass wir Entscheidungen konsequent vom Kundennutzen ausgehend treffen, auch wenn sie intern unbequemer sind.'
          },
          {
            id: 'ST-08',
            text: 'Unsere Kundenzentrierung unterscheidet sich erkennbar vom Wettbewerb und ist Teil unserer Positionierung.'
          },
          {
            id: 'ST-09',
            text: 'Kundenzentrierung ist in unserer Unternehmenskultur verankert und Bestandteil unseres Selbstverständnisses – kein Projekt.'
          }
        ]
      }
    ]
  },
  {
    id: 'loop',
    title: 'Loop der Kundenzentrierung',
    kicker: 'Ebene 2 von 3',
    intro:
      'Wie gut gelingt es uns, Kundenbedürfnisse zu verstehen, Feedback zu messen, zu nutzen und daraus spürbare Verbesserungen zu machen?',
    icon: Repeat,
    subgroups: [
      {
        id: 'loop-main',
        title: null,
        questions: [
          {
            id: 'LO-01',
            text: 'Wir haben klar definierte Zielkundensegmente und richten unser Leistungsversprechen gezielt auf diese aus.'
          },
          {
            id: 'LO-02',
            text: 'Wir treffen bewusste Entscheidungen, für welche Kundenbedürfnisse wir exzellent sein wollen – und für welche nicht.'
          },
          {
            id: 'LO-03',
            text: 'Wir kennen unsere wesentlichen Kundenreisen und wissen, wo Begeisterung und wo Frustration entsteht.'
          },
          {
            id: 'LO-04',
            text: 'Kundenfeedback wird regelmäßig und systematisch (bspw. einmal im Jahr und an zentralen Touchpoints) bei allen Kunden erhoben.'
          },
          {
            id: 'LO-05',
            text: 'Wir haben einen unterschiedlichen Umgang mit marketingorientiertem Feedback (bspw. Google-Bewertungen) und verbesserungsorientiertem Feedback (z. B. aus Befragungen).'
          },
          {
            id: 'LO-06',
            text: 'Wir binden Kunden aktiv in die Weiterentwicklung unserer Leistungen ein – etwa durch direktes Vertesten, Co-Creation oder Formate wie Kundenbeiräte.'
          },
          {
            id: 'LO-07',
            text: 'Wir berücksichtigen auch die Bedürfnisse unserer Kunden in 5 Jahren bei relevanten strategischen Überlegungen.'
          },
          {
            id: 'LO-08',
            text: 'Kundenfeedback wird strukturiert ausgewertet, priorisiert und bearbeitbar gemacht.'
          },
          {
            id: 'LO-09',
            text: 'Datengetrieben leiten wir kundenindividuell die richtige Ansprache mit dem richtigen Thema oder Produkt ab.'
          },
          {
            id: 'LO-10',
            text: 'Kundenfeedback fließt systematisch in die Optimierung von Prozessen und Produkten ein.'
          },
          {
            id: 'LO-11',
            text: 'Wir wandeln auch kritisches Feedback (z. B. Beschwerden) konsequent in für den Kunden spürbare Verbesserungen um.'
          },
          {
            id: 'LO-12',
            text: 'Wir schaffen auf allen Kanälen eine für den Kunden wahrnehmbare, individuelle und einfache Kommunikation.'
          },
          {
            id: 'LO-13',
            text: 'Umgesetzte Verbesserungen sind für die Kunden spürbar, und ihre Wirkung wird sichtbar gemacht.'
          }
        ]
      }
    ]
  },
  {
    id: 'wirkungsebenen',
    title: 'Wirkungsebenen der Kundenzentrierung',
    kicker: 'Ebene 3 von 3',
    intro: 'Ist unsere Organisation wirklich dafür gebaut?',
    icon: Layers,
    subgroups: [
      {
        id: 'mitarbeitende',
        title: 'Mitarbeitende',
        questions: [
          {
            id: 'WE-MA-01',
            text: 'Mit dem Anspruch, Kunden zu begeistern und echte Wow-Momente zu schaffen, haben unsere Mitarbeitenden das passende Mindset und die notwendigen Fähigkeiten.'
          },
          {
            id: 'WE-MA-02',
            text: 'Unsere Mitarbeitenden kennen ihren konkreten Beitrag für den Kunden.'
          },
          {
            id: 'WE-MA-03',
            text: 'Unsere Mitarbeitenden verstehen, was es heißt, sich in die Schuhe des Kunden zu stellen.'
          },
          {
            id: 'WE-MA-04',
            text: 'Unsere Mitarbeitenden im Kundenkontakt kennen die Zufriedenheit ihrer Kunden.'
          },
          {
            id: 'WE-MA-05',
            text: 'Unsere Mitarbeitenden im Kundenkontakt steuern sich aktiv nach der Zufriedenheit ihrer Kunden.'
          },
          {
            id: 'WE-MA-06',
            text: 'Trainings und Entwicklungsformate vermitteln regelmäßig die Kundenperspektive und stärken kundenzentriertes Handeln.'
          },
          {
            id: 'WE-MA-07',
            text: 'Bei der Einstellung neuer Mitarbeitender sind uns der kulturelle Fit und das Mindset, Kunden begeistern zu wollen, mindestens ebenso wichtig wie Fachkenntnisse.'
          }
        ]
      },
      {
        id: 'teams',
        title: 'Teams',
        questions: [
          {
            id: 'WE-TM-01',
            text: 'Unsere Teams arbeiten entlang der Kundenreisen mit klaren Zielen, abgestimmten Beiträgen und einem gelebten Zusammenarbeitsmodell zusammen.'
          },
          {
            id: 'WE-TM-02',
            text: 'Unsere Teams arbeiten bereichsübergreifend „End-to-End“ entlang der Kundenreise zusammen und übernehmen gemeinsame Verantwortung für das Kundenerlebnis – nicht nur für ihr Thema.'
          },
          {
            id: 'WE-TM-03',
            text: 'Unsere Teams nutzen kundenzentrierte Methoden (z. B. Kundenreisen, Design Thinking, Personas, Prototyping), um Kundenbedürfnisse konsequent und iterativ aus der Kundenperspektive in Lösungen zu überführen.'
          },
          {
            id: 'WE-TM-04',
            text: 'Der Anspruch „Nutzererlebnis vor Profitabilität“ wird in der Teamarbeit gelebt.'
          }
        ]
      },
      {
        id: 'fuehrungskraefte',
        title: 'Führungskräfte',
        questions: [
          {
            id: 'WE-FK-01',
            text: 'Kundenzentrierung ist zentraler Bestandteil des Führungsverständnisses und wird von unseren Führungskräften in konkretes Handeln übersetzt.'
          },
          {
            id: 'WE-FK-02',
            text: 'Strategische Leitplanken zur Kundenzentrierung werden von den Führungskräften systematisch in konkrete Erwartungen, Arbeitsweisen und Entscheidungen auf Team- und Bereichsebene übersetzt.'
          },
          {
            id: 'WE-FK-03',
            text: 'Unsere Führungskräfte leben eine kundenbegeisternde Haltung in ihren Aktivitäten und Entscheidungen spürbar vor.'
          },
          {
            id: 'WE-FK-04',
            text: 'Unsere Führungskräfte befähigen ihre Mitarbeitenden, kundenzentrierte Entscheidungen eigenständig und schnell zu treffen.'
          }
        ]
      },
      {
        id: 'gesamtorganisation',
        title: 'Gesamtorganisation',
        questions: [
          {
            id: 'WE-GO-GV-01',
            text: 'Es gibt eine:n klare:n Verantwortliche:n für die aktive Koordination und Weiterentwicklung von Kundenzentrierung.'
          },
          {
            id: 'WE-GO-GV-02',
            text: 'Die Kundensicht ist in relevanten Gremien vertreten – es gibt ein zentrales Format (z. B. CX-Board), das Kundenzentrierung organisationsübergreifend treibt.'
          },
          {
            id: 'WE-GO-ST-01',
            text: 'Wir messen systematisch den Zusammenhang zwischen Kundenzufriedenheit und wirtschaftlichem Erfolg (z. B. Ertrag, Bindung, Cross-Selling).'
          },
          {
            id: 'WE-GO-ST-02',
            text: 'Wir wissen, welche Maßnahmen zur Kundenzentrierung den größten wirtschaftlichen Hebel haben.'
          },
          {
            id: 'WE-GO-ST-03',
            text: 'Kundenzentrierung ist fest in Instrumenten und Steuerungslogiken (z. B. OKR, Feedback, Ziele) der gesamten Organisation verankert.'
          },
          {
            id: 'WE-GO-ST-04',
            text: 'Kundenzufriedenheit ist ein bonusrelevantes Unternehmensziel – mindestens für das Topmanagement.'
          },
          {
            id: 'WE-GO-ST-05',
            text: 'Strukturen, Prozesse und Verantwortlichkeiten unterstützen schnelle, kundenfreundliche Entscheidungen.'
          },
          {
            id: 'WE-GO-ST-06',
            text: 'Das Zusammenspiel von Markt, Marktfolge und Steuerung wird kundenzentriert gelebt, z. B. entlang der Kundenreise.'
          },
          {
            id: 'WE-GO-EN-01',
            text: 'Wir haben die notwendigen Ressourcen und Rahmenbedingungen, um Kundenzentrierung aktiv voranzutreiben.'
          },
          {
            id: 'WE-GO-EN-02',
            text: 'Digitale Lösungen und KI schaffen Freiräume für echte, wertvolle Kundeninteraktion.'
          },
          {
            id: 'WE-GO-EN-03',
            text: 'Wir nutzen dort, wo es sinnvoll und mehrwertstiftend ist, zentrale Angebote aus dem Verbund und entwickeln an den entscheidenden Stellen eigene, kundenzentrierte Lösungen.'
          }
        ]
      }
    ]
  }
];

export function sectionQuestions(s: Section): Question[] {
  return s.subgroups.flatMap((g) => g.questions);
}
export const TOTAL_QUESTIONS = SECTIONS.reduce((n, s) => n + sectionQuestions(s).length, 0);

export const SECTION_BLURB: Record<Section['id'], string> = {
  strategie:
    'Kundenzentrierung braucht eine klare strategische Verankerung in der Strategie und vorgelebte Priorität im Topmanagement-Team, die Kundenzufriedenheit zur zentralen Orientierungsgröße macht.',
  loop: 'Systematisches Kundenfeedback bildet den Motor der kontinuierlichen Verbesserung: verstehen, messen, lernen und optimieren – als geschlossener, dauerhafter Kreislauf.',
  wirkungsebenen:
    'Kundenzentrierung entfaltet ihre Wirkung nur dann nachhaltig, wenn sie auf allen Ebenen verankert ist: bei Mitarbeitenden, in Teams, bei der Führungsmannschaft und in der Gesamtorganisation – strukturell wie kulturell.'
};
